import React, { useEffect, useMemo, useState } from 'react';

import { doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { FaCircle, FaExclamationCircle } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';

import notFoundImg from 'assets/undraw_not_found_white.svg';
import LoadingContainer from 'components/LoadingContainer';
import { useUserData } from 'hooks/auth';
import Achievement from 'interfaces/Achievement';
import UserInfo from 'interfaces/UserInfo';
import UserReputation from 'interfaces/UserReputation';
import { db } from 'services/firebase';
import { COLORS } from 'styles/constants';
import calcUserPoints from 'utils/calcUserPoints';
import { isUserVerified } from 'utils/permissions';

import AchievementModal from './AchievementModal';
import {
  Container,
  ReputationDisplay,
  NotVerifiedDisplay,
  NotVerifiedText,
  AchievementsContainer,
  AchievementDisplay,
  NoAchievementsContainer,
} from './styles';

const Profile: React.FC = () => {
  const history = useHistory();
  const { user } = useUserData();

  const [loading, setLoading] = useState(false);
  const [userInfo, setUserinfo] = useState<UserInfo>();
  const [userReputation, setUserReputation] = useState<UserReputation>();
  const [userVerified, setUserVerified] = useState(true);
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement>();

  const userPoints = useMemo(() => {
    return calcUserPoints(userReputation?.achievements);
  }, [userReputation]);

  const getStorageImgUrl = (id: string): string =>
    `https://firebasestorage.googleapis.com/v0/b/mycircle-app.appspot.com/o/achievement-icons%2F${id}.png?alt=media&token=4867e4ad-4496-4b98-b590-99af40ecf4b0`;

  const handleCloseModal = (): void => {
    setSelectedAchievement(undefined);
  };

  useEffect(() => {
    const loadUserData = async (): Promise<void> => {
      setLoading(true);

      const currentUrl = window.location.href;
      const id = currentUrl.substring(currentUrl.lastIndexOf('/') + 1);

      const userInfoDoc = await getDoc(doc(db, 'userinfo', id));
      const userReputationDoc = await getDoc(doc(db, 'reputation', id));

      const userInfoData = userInfoDoc.data() as UserInfo;
      const userReputationData = userReputationDoc.data() as UserReputation;

      if (!userInfoData || !userReputationData) {
        toast.error(
          'Erro ao carregar informações do perfil. Recarregue a página e tente novamente',
          { duration: 2000 },
        );
        history.push('/');
        return;
      }

      setUserVerified(await isUserVerified(userInfoData.uid));

      setUserinfo(userInfoData);
      setUserReputation(userReputationData);
      setLoading(false);
    };

    loadUserData();
  }, [history]);

  return (
    <>
      {userInfo && userReputation && !loading ? (
        <Container>
          <img src={userInfo.photoURL} alt="Perfil do usuário" />
          <h2>{userInfo.displayName}</h2>
          <h4>Membro(a) do circulo {userInfo.circle.name}</h4>

          {userVerified ? (
            <ReputationDisplay>
              <FaCircle color={COLORS.primary} />
              <div style={{ width: 7 }} />
              {userPoints && <p>{userPoints} pontos</p>}
            </ReputationDisplay>
          ) : (
            <>
              <NotVerifiedDisplay
                clickable={userInfo.uid === user?.uid}
                onClick={() => {
                  if (userInfo.uid !== user?.uid) {
                    return;
                  }
                  const win = window.open(
                    'https://2rht68app8p.typeform.com/circle-verify',
                    '_blank',
                  );
                  win && win.focus();
                }}
              >
                <p>
                  <FaExclamationCircle color="red" size={25} />
                  <div style={{ width: 10 }} />
                  <span>Usuário não verificado</span>
                </p>
                {userInfo.uid === user?.uid && (
                  <p>(Clique aqui para solicitar a verificação)</p>
                )}
              </NotVerifiedDisplay>
              <NotVerifiedText>
                Usuários não verificados não podem avaliar serviços da
                plataforma
              </NotVerifiedText>
            </>
          )}

          <h3>Conquistas </h3>
          {userReputation.achievements.length > 0 ? (
            <AchievementsContainer>
              {userReputation.achievements.map((achievement) => (
                <AchievementDisplay
                  key={achievement.id}
                  onClick={() => setSelectedAchievement(achievement)}
                >
                  <img
                    src={getStorageImgUrl(achievement.id)}
                    alt={achievement.id}
                  />
                  <div style={{ width: 20 }} />
                  <div>
                    <h4>{achievement.name}</h4>
                    <p>
                      <FaCircle color={COLORS.primary} />{' '}
                      <div style={{ width: 10 }} /> {achievement.points}
                    </p>
                  </div>
                </AchievementDisplay>
              ))}
            </AchievementsContainer>
          ) : (
            <NoAchievementsContainer>
              <img
                src={notFoundImg}
                alt="Serviço sem recomendações"
                width={200}
              />
              <p>Este usuário ainda não tem nenhuma conquista</p>
            </NoAchievementsContainer>
          )}

          {selectedAchievement && (
            <AchievementModal
              achievement={selectedAchievement}
              isOpen={!!selectedAchievement}
              imgUrl={getStorageImgUrl(selectedAchievement.id)}
              handleCloseModal={handleCloseModal}
            />
          )}
        </Container>
      ) : (
        <LoadingContainer color="#fff" />
      )}
    </>
  );
};

export default Profile;
